// src/contexts/token/TokenDataProvider.tsx
/**
 * TokenDataProvider - Unified token data context
 * 
 * Composes all domain-specific providers into a single entry point:
 * - Price history (token + benchmarks)
 * - Fundamentals (fees, TVL, DAU, ratios)
 * - Derivatives (OI, funding rates)
 * 
 * Usage:
 *   <TokenDataProvider ticker="SOL">
 *     <TokenDashboard />
 *   </TokenDataProvider>
 * 
 * In components:
 *   const { price, fundamentals, derivatives } = useTokenData();
 *   
 * Or use domain-specific hooks:
 *   const { token, aligned } = useTokenPrice();
 *   const { fees, current } = useTokenFundamentals();
 *   const { openInterest, hasData } = useTokenDerivatives();
 */

import React, { createContext, useMemo } from "react";
import { TokenPriceProvider } from "./TokenPriceProvider";
import { useTokenPriceOptional } from "./useTokenPrice";
import { TokenFundamentalsProvider } from "./TokenFundamentalsProvider";
import { useTokenFundamentalsOptional } from "./useTokenFundamentals";
import { TokenDerivativesProvider } from "./TokenDerivativesProvider";
import { useTokenDerivativesOptional } from "./useTokenDerivatives";
import type { TokenDataContextValue } from "./types";

// ============================================================================
// CONTEXT
// ============================================================================

const TokenDataContext = createContext<TokenDataContextValue | null>(null);

// ============================================================================
// INNER COMPONENT - Collects all context values
// ============================================================================

interface TokenDataCollectorProps {
  ticker: string;
  children: React.ReactNode;
}

const TokenDataCollector: React.FC<TokenDataCollectorProps> = ({ ticker, children }) => {
  const price = useTokenPriceOptional();
  const fundamentals = useTokenFundamentalsOptional();
  const derivatives = useTokenDerivativesOptional();
  
  const value = useMemo<TokenDataContextValue | null>(() => {
    // All providers must be available
    if (!price || !fundamentals || !derivatives) {
      return null;
    }
    
    return {
      ticker: ticker.toUpperCase(),
      price,
      fundamentals,
      derivatives,
      isLoading: price.isLoading || fundamentals.isLoading || derivatives.isLoading,
      isError: price.isError || fundamentals.isError || derivatives.isError,
      refetch: () => {
        price.refetch();
        fundamentals.refetch();
        derivatives.refetch();
      },
    };
  }, [ticker, price, fundamentals, derivatives]);
  
  if (!value) {
    // Still loading or missing providers
    return <>{children}</>;
  }
  
  return (
    <TokenDataContext.Provider value={value}>
      {children}
    </TokenDataContext.Provider>
  );
};

// ============================================================================
// PROVIDER
// ============================================================================

interface TokenDataProviderProps {
  ticker: string;
  /** Benchmark tickers for comparison (default: ['BTC', 'ETH']) */
  benchmarks?: string[];
  /** 
   * Which data domains to include (default: all)
   * Use this to optimize if you don't need all data
   */
  include?: {
    price?: boolean;
    fundamentals?: boolean;
    derivatives?: boolean;
  };
  children: React.ReactNode;
}

export const TokenDataProvider: React.FC<TokenDataProviderProps> = ({
  ticker,
  benchmarks = ['BTC', 'ETH'],
  include = { price: true, fundamentals: true, derivatives: true },
  children,
}) => {
  // Compose providers based on what's needed
  let content = (
    <TokenDataCollector ticker={ticker}>
      {children}
    </TokenDataCollector>
  );
  
  // Wrap with derivatives if needed
  if (include.derivatives !== false) {
    content = (
      <TokenDerivativesProvider ticker={ticker}>
        {content}
      </TokenDerivativesProvider>
    );
  }
  
  // Wrap with fundamentals if needed
  if (include.fundamentals !== false) {
    content = (
      <TokenFundamentalsProvider ticker={ticker}>
        {content}
      </TokenFundamentalsProvider>
    );
  }
  
  // Wrap with price (almost always needed)
  if (include.price !== false) {
    content = (
      <TokenPriceProvider ticker={ticker} benchmarks={benchmarks}>
        {content}
      </TokenPriceProvider>
    );
  }
  
  return content;
};

export { TokenDataContext };

